/**
 * PlanEditorModal — fullscreen editor for editing plan content before approving.
 * Uses a plain textarea for now; can be upgraded to TipTap/rich editor later.
 */
import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"

interface PlanEditorModalProps {
  open: boolean
  content: string
  onSave: (markdown: string) => void
  onCancel: () => void
}

export function PlanEditorModal({ open, content, onSave, onCancel }: PlanEditorModalProps) {
  const { t } = useTranslation()
  const [edited, setEdited] = useState(content)

  // Sync when content changes externally
  useEffect(() => {
    if (open) {
      setEdited(content)
    }
  }, [open, content])

  const handleSave = useCallback(() => {
    onSave(edited)
  }, [edited, onSave])

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel()
      }}
    >
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("deepExplorer.plan_preview.edit_plan")}</DialogTitle>
        </DialogHeader>
        <Textarea
          className="min-h-[300px] flex-1 resize-none font-mono text-sm"
          value={edited}
          onChange={(e) => setEdited(e.target.value)}
        />
        <DialogFooter className="flex flex-row gap-2 sm:justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            {t("deepExplorer.plan_preview.cancel")}
          </Button>
          <Button size="sm" onClick={handleSave}>
            {t("deepExplorer.plan_preview.save_and_approve")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
